import { Body, Icon, Link, VStack, transition } from '@hover/blueprint';
import { iconShop } from '@hover/icons';
import { motion } from 'framer-motion';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import { imageTracking } from 'src/modules/homeownerApp/components/Customize/tracking';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import { isDevelopment } from 'src/shared/helpers/environment';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';

import { GOOGLE_LENS_URL, HOVER_URL } from '../../constants';
import type { AiOrSampleGeneratedImage } from '../../types';

const GOT_IT_KEY = 'canShopGotIt';

interface LensButtonProps {
  image: AiOrSampleGeneratedImage;
}

const MotionVStack = motion(VStack);

export const LensButton = ({ image }: LensButtonProps) => {
  const [gotIt, setGotIt] = useState(false);
  const { trackingData } = useHomeownerAppContext();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { projectId } = useParams<{ projectId: string }>();
  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();
  const imageUrl = isDevelopment()
    ? `${HOVER_URL}${image?.generatedImagePath}`
    : image?.generatedImagePath;
  const lensUrl = `${GOOGLE_LENS_URL}${imageUrl}`;

  const trackingProperties = useMemo(
    () => ({
      ...trackingData,
      ...imageTracking(image),
      partner_id: partnerId,
      primary_cta: false,
      project_id: Number(projectId),
      destination_url: lensUrl,
      link_text: 'shopping-basket-icon',
      track_id: 'shop-image-link',
    }),
    [image, lensUrl, partnerId, projectId, trackingData],
  );

  const impressionProperties = useMemo(
    () => ({
      ...trackingProperties,
      button_text: 'shopping-basket-icon',
      track_id: 'shop-image-button',
    }),
    [trackingProperties],
  );

  const handleGotItClick = () => {
    setGotIt(true);
    localStorage.setItem(GOT_IT_KEY, 'true');
  };

  useEffect(() => {
    try {
      setGotIt(localStorage.getItem(GOT_IT_KEY) === 'true');
    } catch (e) {
      localStorage.removeItem(GOT_IT_KEY);
    }
  }, []);

  return (
    <MotionVStack
      alignItems="flex-start"
      backdropFilter="blur(25px)"
      background="darkAlpha.300"
      borderRadius={350}
      overflow="hidden"
      p={300}
      position="relative"
      sx={{
        ...transition.easing250('opacity'),
        '&[disabled]': { color: 'neutral.0' },
        color: 'rgba(255, 255, 255, 0)',
        fontSize: '1px',
        width: '40px',
        height: '40px',
      }}
      transition={{
        type: 'easeInOut',
        duration: 0.2,
      }}
      whileHover={
        !gotIt
          ? {
              color: 'rgba(255, 255, 255, 1)',
              fontSize: '14px',
              width: '203px',
              height: '173px',
            }
          : {}
      }
    >
      <VStack
        alignItems="flex-start"
        flex={1}
        gap={200}
        maxW="180px"
        pl={400}
        pr={300}
        pt={500}
      >
        <Body color="inherit" fontSize={200} fontWeight={700} m={0}>
          {t('fullscreenImage.shopDesignIdeas')}
        </Body>
        <Body color="inherit" fontSize={200} m={0}>
          {t('fullscreenImage.seeSomethingYouLike')}
        </Body>
        <Link
          as="span"
          color="inherit"
          cursor="pointer"
          onClick={handleGotItClick}
          sx={{
            '&:hover': { color: 'neutral.300' },
          }}
          textDecoration="underline"
        >
          {t('fullscreenImage.gotIt')}
        </Link>
      </VStack>
      <Button
        as="a"
        background="none"
        bottom={200}
        h={400}
        href={lensUrl}
        includeTrackingImpression
        label="shop-image-link"
        m={0}
        onClick={e => e.stopPropagation()}
        optionalImpressionProperties={impressionProperties}
        optionalTrackingProperties={trackingProperties}
        p={0}
        position="absolute"
        right={200}
        sx={{ '&:hover': { background: 'none !important' } }}
        target="_blank"
        w={400}
      >
        <Icon
          color="neutral.0"
          icon={iconShop}
          sx={{ '&:hover': { color: 'neutral.300' } }}
        />
      </Button>
    </MotionVStack>
  );
};
